import React, { useState, useEffect } from 'react';
import api from '@/api';
import { useAuth } from '@/contexts/AuthContext';
import { FormField } from './FormField';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Loader2, Plus, Edit, Trash2, Shield } from 'lucide-react';
import { toast } from 'sonner';

const ROLE_COLORS = {
  superadmin: 'bg-[#c9a84c]/20 text-[#c9a84c]',
  admin: 'bg-blue-900/30 text-blue-400',
  viewer: 'bg-gray-800 text-gray-400',
};

export default function AdminUsers() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [panelOpen, setPanelOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'admin' });
  const [saving, setSaving] = useState(false);

  const isSuperAdmin = currentUser?.role === 'superadmin';

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin-users');
      setUsers(res.data);
    } catch { } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm({ name: '', email: '', password: '', role: 'admin' }); setPanelOpen(true); };
  const openEdit = (u) => { setEditing(u.id); setForm({ name: u.name, email: u.email, password: '', role: u.role, is_active: u.is_active }); setPanelOpen(true); };

  const save = async () => {
    if (!form.name || !form.email) { toast.error('Nome e email obbligatori'); return; }
    if (!editing && !form.password) { toast.error('Password obbligatoria per nuovo utente'); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.password) delete payload.password;
      if (editing) {
        await api.put(`/admin-users/${editing}`, payload);
        toast.success('Utente aggiornato');
      } else {
        await api.post('/admin-users', payload);
        toast.success('Utente creato');
      }
      setPanelOpen(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (id === currentUser?.sub) { toast.error('Non puoi eliminare il tuo account'); return; }
    if (!confirm('Eliminare questo utente admin?')) return;
    await api.delete(`/admin-users/${id}`);
    load();
    toast.success('Utente eliminato');
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="label-gold">Gestione</p>
          <h2 className="font-serif text-3xl text-white font-light mt-1">Utenti Admin</h2>
        </div>
        {isSuperAdmin && (
          <button onClick={openCreate} className="btn-gold flex items-center gap-2" data-testid="new-admin-user-btn">
            <Plus size={14} /> Nuovo Utente
          </button>
        )}
      </div>
      {loading ? <div className="flex justify-center py-16"><Loader2 size={28} className="animate-spin text-[#c9a84c]" /></div> : (
        <div className="card-dark rounded-lg overflow-hidden">
          <table className="w-full table-dark">
            <thead><tr>
              <th className="text-left">Nome</th>
              <th className="text-left hidden sm:table-cell">Email</th>
              <th className="text-left">Ruolo</th>
              <th className="text-left hidden md:table-cell">Stato</th>
              <th />
            </tr></thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id}>
                  <td>
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 bg-[var(--lux-surface-3)] rounded-full flex items-center justify-center">
                        <Shield size={12} className="text-[#c9a84c]" />
                      </div>
                      <span className="text-white">{u.name}</span>
                    </div>
                  </td>
                  <td className="hidden sm:table-cell text-[var(--lux-text-sec)] text-xs">{u.email}</td>
                  <td><span className={`status-badge ${ROLE_COLORS[u.role] || ''}`}>{u.role}</span></td>
                  <td className="hidden md:table-cell">
                    <span className={`w-2 h-2 rounded-full inline-block ${u.is_active ? 'bg-green-500' : 'bg-[var(--lux-border-3)]'}`} />
                  </td>
                  <td>
                    {isSuperAdmin && (
                      <div className="flex gap-1 justify-end">
                        <button onClick={() => openEdit(u)} className="p-1.5 text-[var(--lux-text-muted-3)] hover:text-white"><Edit size={13} /></button>
                        <button onClick={() => remove(u.id)} className="p-1.5 text-[var(--lux-text-muted-3)] hover:text-red-400"><Trash2 size={13} /></button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Sheet open={panelOpen} onOpenChange={setPanelOpen}>
        <SheetContent className="bg-[var(--lux-surface-1)] border-l border-[#222] text-white w-full sm:max-w-sm overflow-y-auto">
          <SheetHeader className="mb-6">
            <SheetTitle className="font-serif text-2xl font-light text-white">{editing ? 'Modifica' : 'Nuovo'} Utente Admin</SheetTitle>
          </SheetHeader>
          <div className="space-y-4">
            <FormField label="Nome *" name="name" form={form} setForm={setForm} testId="user-field-name" />
            <FormField label="Email *" name="email" type="email" form={form} setForm={setForm} testId="user-field-email" />
            <FormField label={editing ? "Nuova password (lascia vuoto per non cambiare)" : "Password *"} name="password" type="password" form={form} setForm={setForm} testId="user-field-password" />
            <div>
              <label className="label-muted block mb-1">Ruolo</label>
              <select className="input-dark" value={form.role} onChange={e => setForm(p => ({ ...p, role: e.target.value }))} data-testid="user-role-select">
                <option value="viewer">Viewer (sola lettura)</option>
                <option value="admin">Admin</option>
                <option value="superadmin">Superadmin</option>
              </select>
            </div>
            {editing && (
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={form.is_active ?? true} onChange={e => setForm(p => ({ ...p, is_active: e.target.checked }))} className="accent-[#c9a84c]" />
                <span className="text-sm text-[var(--lux-text-sec)]">Attivo</span>
              </label>
            )}
            <div className="flex gap-3 pt-4 border-t border-[#222]">
              <button onClick={save} disabled={saving} className="btn-gold flex-1 flex items-center justify-center gap-2">
                {saving && <Loader2 size={13} className="animate-spin" />}
                Salva
              </button>
              <button onClick={() => setPanelOpen(false)} className="btn-outline-gold flex-1">Annulla</button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
